import React, { useState, useEffect, useContext } from 'react'
import { Form } from 'semantic-ui-react'
import { PaymentElement } from '@stripe/react-stripe-js'
import { Moment } from 'moment'
import { CampProps, SiteProps } from '../../interfaces'
import { validElectric, validRigType } from '../../types'
import api2 from '../../api2'
import { CAMPING_STYLES, discountType } from '../../constants'
import { BookerContext } from '../../contexts/BookerContext'
import isValidEmail from '../../utils/isValidEmail'
import { useDebouncedCallback } from 'use-debounce'
import { bySort } from '../../utils/sorts'
import { useCookies } from 'react-cookie'
import { userParamsActionTypes } from '../../reducers/userParamsReducer'
import PurchaseBottomBar from './PurchaseBottomBar'
import Spinner from '../ui/Spinner'
import { Transition } from '@headlessui/react'
import CheckboxWithLabel from '../ui/CheckboxWithLabelForForm'
import PhoneInputField from '../ui/Admin/PhoneInputField'
import handlePhoneNumberChange from '../../utils/admin/handlePhoneNumberChange'

interface ComponentProps {
  camp: CampProps

  site: SiteProps | null
  startDate: Moment
  endDate: Moment
  setIsPaying: any

  discountType: discountType
  discountId: string

  userRigType: validRigType
  userRigLength: number
  userElectric: validElectric
  userPassengers: number
  userKidCampers: number

  onError: (errorMessage: string) => void

  setIsLoadingCamper
  selectedCampingStyle
  processing
  setDisabled
  setProcessing
  setIsBlocked
  isBlocked
  handleSubmit
  fromAdParam
  setFromAd
  notesAndCustomAnswers
  setNotesAndCustomAnswers
  setIsLoadingIntent
  userParams
  userParamsDispatch
  isPurchaseButtonDisabled
  stripeSuccessCount
  errorMessage
  setErrorMessage
}

const CheckoutForm = (props: ComponentProps) => {
  const { camp, userRigYear, trackForCamp, trackForPark, campingStyle, isSavingPaymentMethod, setIsSavingPaymentMethod } = useContext(BookerContext)

  const [validCard, setValidCard] = useState(false)
  const [hasBegunCheckout, setHasBegunCheckout] = useState(false)
  const [cookies, setCookie] = useCookies(["from_ad"])
  const queryParams = new URLSearchParams(decodeURIComponent(window.location.search))
  const [isPaymentElementLoaded, setIsPaymentElementLoaded] = useState(false)

  useEffect(() => {
    if (props.fromAdParam) {
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + 30) // Set cookie to expire in 30 days

      setCookie("from_ad", "true", { path: "/", expires: expirationDate })
    }

    if (props.fromAdParam || cookies.from_ad === 'true') {
      props.setFromAd(true)
    }
  }, [])

  useEffect(() => {
    checkForCamper()
  }, [props.userParams?.email, props.userParams?.phone_number, props.userParams?.license_plate])

  const fetchCamper = useDebouncedCallback(() => {
    api2.Camps.findCamperByData({
      campId: camp.id,
      email: props.userParams.email,
      phone: props.userParams.phone,
      license_plate: props.userParams.license_plate,
    })
      .then(({ campers }) => {
        if (campers[0]?.blocked_at) props.setIsBlocked(true)
        props.setIsLoadingCamper(false)
      })
      .catch((err) => {
        console.error(err)
        props.setIsLoadingCamper(false)
      })
  }, 500)

  const checkForCamper = () => {
    if (!isValidEmail(props.userParams.email) && (!props.userParams.phone || !props.userParams.phone_number) && !props.userParams.license_plate) return

    props.setIsLoadingCamper(true)
    fetchCamper()
  }

  useEffect(() => {
    if (!(props.site?.id && props.startDate && props.endDate)) return

    props.setErrorMessage('')
    props.setIsLoadingIntent(true)
  }, [props.site?.id, props.startDate, props.endDate, props.discountType, props.discountId])

  useEffect(() => {
    props.userParamsDispatch({
      type: userParamsActionTypes.vehicle_rig_type,
      payload: { vehicle_rig_type: props.userRigType },
    })

    props.userParamsDispatch({
      type: userParamsActionTypes.vehicle_rig_length,
      payload: {
        vehicle_rig_length: props.userRigLength ? `${props.userRigLength} ft` : props.userRigLength,
      },
    })

    props.userParamsDispatch({
      type: userParamsActionTypes.vehicle_electric,
      payload: { vehicle_electric: props.userElectric },
    })

    props.userParamsDispatch({
      type: userParamsActionTypes.vehicle_year,
      payload: { vehicle_year: userRigYear },
    })
  }, [props.userRigType, props.userRigLength, props.userElectric, userRigYear])

  useEffect(() => {
    props.setIsPaying(props.processing)
  }, [props.processing])

  useEffect(() => {
    const requiredFieldsFilled = props
      .selectedCampingStyle
      .custom_fields?.filter((field) => field.required)
      ?.every((field) => {
        const actionType = `UPDATE_${field.camper_attribute_name?.toUpperCase()}`
        if (Object.values(userParamsActionTypes).includes(actionType)) {
          return !!props.userParams[field.camper_attribute_name]
        } else {
          return !!props.notesAndCustomAnswers[field.label]
        }
      })

    const isSavingPaymentMethodRequired = props.selectedCampingStyle?.save_booker_payment_method
    const paymentMethodConditionMet = !isSavingPaymentMethodRequired || isSavingPaymentMethod

    if (
      props.userParams.first_name &&
      props.userParams.last_name &&
      isValidEmail(props.userParams.email) &&
      Number(props.userParams.phone?.match(/\d/g)?.length) >= 7 &&
      validCard &&
      paymentMethodConditionMet &&
      requiredFieldsFilled
    ) {
      props.setDisabled(false)
    } else {
      props.setDisabled(true)
    }
  }, [props.userParams, validCard, props.notesAndCustomAnswers, isSavingPaymentMethod])

  useEffect(() => {
    if (props.isBlocked) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove("overflow-hidden")
    }

    // Cleanup function to remove the class when the component is unmounted or hidden
    return () => {
      document.body.classList.remove("overflow-hidden")
    }
  }, [props.isBlocked]) // Runs only when `props.isBlocked` changes

  useEffect(() => {
    if (props.stripeSuccessCount > 1) {
      Sentry.captureMessage("Multiple Stripe calls made", {
        level: Sentry.Severity.Fatal,
        extra: {
          camp: camp.name,
          site: props.site?.name,
          startDate: props.startDate.toISOString(),
          endDate: props.endDate.toISOString(),
          userEmail: props.userParams.email,
        }
      })
    }
  }, [props.stripeSuccessCount])

  const handleCreditCardChange = async (event) => {
    setIsPaymentElementLoaded(true)
    setValidCard(!event.error && event.complete)
    props.setErrorMessage(event.error ? event.error.message : props.errorMessage)
    checkForCamper()
  }

  // TODO: remove these once we convert custom fields
  const isReservingBoatSlip = props.selectedCampingStyle.name === CAMPING_STYLES.BOAT_SLIP
  const isReservingRVSite = props.selectedCampingStyle.name === CAMPING_STYLES.RV

  return (
    <Form onSubmit={props.handleSubmit} error={!!props.errorMessage}>
      <Transition
        show={props.processing}
        enter='transition-opacity ease-out duration-75'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity ease-in duration-75'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        <div className='fixed inset-0 z-50 bg-white bg-opacity-80 flex items-center justify-center text-sm'>
          <Spinner size={5} />
          Loading...
        </div>
      </Transition>
      <div className='font-bold text-lg mb-6'>Payment Details</div>
      <Form.Group widths='equal'>
        <Form.Input
          fluid
          required
          label="First Name"
          autoComplete="given-name"
          onChange={(e) => {
            props.setErrorMessage("")
            if (!hasBegunCheckout) {
              trackForCamp("begin_checkout")
              trackForPark("begin_checkout", {
                camp: props.camp.name,
              })
              setHasBegunCheckout(true)
            }
            props.userParamsDispatch({
              type: userParamsActionTypes.first_name,
              payload: { first_name: e.target.value },
            })
          }}
        />
        <Form.Input
          fluid
          required
          label="Last Name"
          autoComplete="family-name"
          onChange={(e) => {
            props.setErrorMessage('')
            props.userParamsDispatch({
              type: userParamsActionTypes.last_name,
              payload: { last_name: e.target.value },
            })
          }}
        />
      </Form.Group>

      <Form.Group widths="equal">
        <Form.Input
          fluid
          required
          label="Email"
          autoComplete="email"
          onChange={(e) => {
            props.setErrorMessage('')
            props.userParamsDispatch({
              type: userParamsActionTypes.email,
              payload: { email: e.target.value },
            })
          }}
        />
      </Form.Group>

      <div className="field required pb-8">
        <label htmlFor="phone">Phone</label>
        <PhoneInputField
          inputStyle={{ paddingLeft: 42 }}
          value={`${props.userParams.phone_country_code}${props.userParams.phone_number}`}
          camp={props.camp}
          onChange={(phone, country) => {
            const { phone_number, phone_country_code } = handlePhoneNumberChange(phone, country)

            props.userParamsDispatch({
              type: userParamsActionTypes.phone,
              payload: { phone },
            })
            props.userParamsDispatch({
              type: userParamsActionTypes.phone_number,
              payload: { phone_number },
            })
            props.userParamsDispatch({
              type: userParamsActionTypes.phone_country_code,
              payload: { phone_country_code },
            })
          }}
          onBlur={(e) => {
            if (e.target.value.match(/\d/g)?.length <= 10) {
              props.setErrorMessage('Please enter a valid phone number with area code')
            } else {
              props.setErrorMessage(null)
            }
          }}
        />
      </div>

      {/* TODO: please remove this after we use custom fields for these */}
      {isReservingBoatSlip && (
        <>
          <div className="py-4 font-semibold">Boat info</div>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              required
              label="Boat year"
              onChange={(e) => {
                props.setErrorMessage('')
                props.userParamsDispatch({
                  type: userParamsActionTypes.vehicle_year,
                  payload: { vehicle_year: e.target.value },
                })
              }}
              value={props.userParams.vehicle_year}
            />
            <Form.Input
              fluid
              required
              label="Boat model"
              onChange={(e) => {
                props.setErrorMessage('')
                props.userParamsDispatch({
                  type: userParamsActionTypes.vehicle_make,
                  payload: { vehicle_make: e.target.value },
                })
              }}
              value={props.userParams.vehicle_make}
            />
            <Form.Input
              fluid
              required
              label="Boat color"
              onChange={(e) => {
                props.setErrorMessage('')
                props.userParamsDispatch({
                  type: userParamsActionTypes.vehicle_color,
                  payload: { vehicle_color: e.target.value },
                })
              }}
              value={props.userParams.vehicle_color}
            />
          </Form.Group>
        </>
      )}

      {/* TODO: Same here: convert this to a custom field */}
      {props.camp.requires_license_plate && (isReservingRVSite || isReservingBoatSlip) && (
        <Form.Group widths="equal">
          {isReservingBoatSlip && (
            <Form.Input
              fluid
              required
              label="Boat registration number"
              onChange={(e) => {
                props.setErrorMessage('')
                props.setNotesAndCustomAnswers({
                  ...props.notesAndCustomAnswers,
                  boat_registration: e.target.value,
                })
              }}
              value={props.notesAndCustomAnswers['boat_registration']}
            />
          )}

          <Form.Input
            fluid
            required
            label={isReservingBoatSlip ? "Trailer license plate" : "License plate"}
            onChange={(e) => {
              props.setErrorMessage('')
              props.userParamsDispatch({
                type: userParamsActionTypes.license_plate,
                payload: { license_plate: e.target.value },
              })
            }}
            value={props.userParams.license_plate}
          />
        </Form.Group>
      )}

      {props
        .selectedCampingStyle
        .custom_fields?.sort(bySort)
        ?.map((field) => (
          <>
            <Form.Group widths='equal'>
              <Form.Input
                fluid
                required={field.required}
                label={field.label}
                onChange={(e) => {
                  props.setErrorMessage('')

                  const actionType = `UPDATE_${field.camper_attribute_name?.toUpperCase()}`

                  if (Object.values(userParamsActionTypes).includes(actionType)) {
                    props.userParamsDispatch({
                      type: actionType,
                      payload: { [field.camper_attribute_name]: e.target.value },
                    })
                  } else {
                    props.setNotesAndCustomAnswers({
                      ...props.notesAndCustomAnswers,
                      [field.label]: e.target.value,
                    })
                  }
                }}
                value={props.notesAndCustomAnswers[field.label]}
              />
            </Form.Group>
          </>
        ))}

      <Form.Field required>
        <label>Payment Information</label>
        <div className="flex flex-col w-full gap-2">
          <PaymentElement id="card-element" onChange={handleCreditCardChange} />
        </div>
        {isPaymentElementLoaded ? (
          <></>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size={6} />
          </div>
        )}
      </Form.Field>
      {(campingStyle.save_booker_payment_method || campingStyle.save_booker_payment_method === null) && (
        <CheckboxWithLabel
          label="Save my payment method for future use"
          value={isSavingPaymentMethod}
          required={campingStyle.save_booker_payment_method}
          setValue={(e) => {
            setIsSavingPaymentMethod(e)
            props.userParamsDispatch({
              type: userParamsActionTypes.is_saving_payment_method,
            })
          }}
          labelClasses="text-md whitespace-nowrap my-2"
          data-testid="save-card-checkbox"
        />
      )}

      <PurchaseBottomBar
        selectedCampingStyle={props.selectedCampingStyle || campingStyle}
        disabledPurchase={props.isPurchaseButtonDisabled}
        errorMessage={props.errorMessage}
        isBlocked={props.isBlocked}
        handleSubmit={props.handleSubmit}
      />
    </Form>
  )
}

export default CheckoutForm
